import * as THREE from 'three';
import {
  panoAt,
  towardDirection,
  type Lesson,
  type PanoPhoto,
  type PanoVariants,
} from '@vrlearning/lesson-schema';
import { makeSun } from './look/sun.js';
import { ASTC_EXTENSION, NO_PANO_CAPS, type PanoCaps } from './pano-caps.js';
import { astcCube, compressedCubeTexture, type AstcCube } from './pano-astc.js';
import { cubeFace, facesFromBlob, type PanoFaces } from './pano-faces.js';
import { markPanoShown } from './pano-hud.js';
import { PANO_CUBE_FACE_LIMIT, panoSource, type PanoSource } from './pano-source.js';
import { MEDIA_CACHE, mediaCacheKey } from '../media-cache.js';
import type { PanoAnswer, PanoAsk } from './pano-work.js';
import { paintOnCanvas, type Painter } from './posters.js';
import { STUDIO_HORIZON } from './studio.js';

/**
 * A 360° photo as a lesson's place (ADR-036,
 * docs/superpowers/specs/2026-09-30-360-photos-design.md, "Track H").
 *
 * The photo is the scene's background, at infinity: a teleport never moves
 * it, and it has no depth, parallax or fog. The lesson stands on a world's
 * 6 m stage in front of it, on a disc in the colour of the photo's ground
 * that hides the tripod and catches the one soft shadow, lit by the photo
 * itself (a PMREM of a small copy) and a sun from its brightest region.
 *
 * This file holds the pure maths first -- tested without WebGL --, then the
 * loading, the uploads and the view that runs a lesson's photos.
 */

/** The sun: its strength, how high it may stand, and when there is none to find. */
export const PANO_SUN = {
  intensity: 1.5,
  minElevation: 35,
  maxElevation: 70,
  /** The brightest block must be this much brighter than the upper half's mean. */
  overcast: 1.25,
  /** Overcast or indoors: from high up, ahead. */
  fallback: { azimuth: 0, elevation: 70 },
} as const;

/** The ground disc: the stage, solid to 4.8 m and gone at 6 m. */
export const PANO_DISC = { radius: 6, solid: 4.8, segments: 96, y: 0.002, alphaSize: 256 } as const;
/** The shadow catcher on it, as the studio's (STUDIO_SHADOW.opacity). */
export const PANO_SHADOW = { radius: 4.8, y: 0.004, opacity: 0.22 } as const;
/** The copy the light is made from: a 256 px PMREM cube, as a world's 1k sky. */
export const PANO_LIGHT = { width: 1024, height: 512 } as const;
/** The copy the ground's colour and the sun are read from; rows 26–28 lie 60–75° below the horizon. */
export const PANO_SAMPLE = { width: 64, height: 32, groundRows: [26, 28] } as const;
/** How long a step's black waits for a late photo before the studio stands in. */
export const PANO_WAIT_SECONDS = 3;
/** How long a lesson's start waits for its first photo, as for a world. */
export const PANO_START_SECONDS = 15;
/** What the black says after 2 s of waiting for a photo. */
export const PANO_HINT = 'De foto wordt geladen…';
/** A direction hotspot's badge: 7 m out from the eye, 0.4 m across (3.3°). */
export const PANO_SPOT = { distance: 7, size: 0.4 } as const;
/**
 * The credit plate: on the disc 1.5 m behind the start, 30° off the floor
 * towards it, letters 5 cm on a canvas of at least 1024 px a metre (docs/04).
 */
export const PANO_CREDIT = {
  width: 1.2,
  height: 0.12,
  behind: 1.5,
  tilt: 30,
  letters: 0.05,
  canvas: { width: 1280, height: 128 },
} as const;
/**
 * The desktop note when the studio stands in for a photo: one that cannot be
 * had, a wish nobody filled, or one merely late (as the black's hint says it;
 * it goes in at the next fade). Taken back when a photo shows.
 */
export const PANO_NOTE = {
  failed: '360°-foto niet geladen: de les staat in de studio',
  unchosen: '360°-foto nog niet gekozen',
  late: '360°-foto wordt nog geladen…',
} as const;

export { cubeFace };

/**
 * The light's 1024 × 512 copy as a texture, for the PMREM: sRGB, linear, no
 * mipmaps, repeating round the seam, and not flipped again: the bitmap was
 * decoded flipped, since WebGL ignores flipY for an ImageBitmap. (The photo
 * itself never goes up as a 2D texture: see cubeTexture.)
 */
export function photoTexture(image: ImageBitmap): THREE.Texture {
  const texture = new THREE.Texture(image);
  texture.mapping = THREE.EquirectangularReflectionMapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 1;
  // Round the seam: clamped, the column at ±180° would sample only one side
  // and show a 1 px line at the back, in the cube and in the light alike.
  texture.wrapS = THREE.RepeatWrapping;
  texture.flipY = false;
  texture.needsUpdate = true;
  return texture;
}

/**
 * The background: the six faces the worker made (pano-faces.ts), as they are.
 * sRGB and never tone-mapped (the background of an sRGB texture is not),
 * linear, no mipmaps -- a photo at infinity is magnified, never minified
 * (16 px a degree at 5760 px against the Quest 3's 25) -- and not flipped:
 * the faces are in WebGL's order, first row first. Faces never wrap, so no
 * seam: the worker sampled round ±180°.
 */
export function cubeTexture(faces: PanoFaces): THREE.CubeTexture {
  const images = faces.data.map((data) => {
    const face = new THREE.DataTexture(data, faces.size, faces.size);
    face.colorSpace = THREE.SRGBColorSpace;
    return face;
  });
  const texture = new THREE.CubeTexture(images);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.format = THREE.RGBAFormat;
  texture.type = THREE.UnsignedByteType;
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = 1;
  texture.flipY = false;
  texture.needsUpdate = true;
  return texture;
}

/**
 * The background's and the light's turn about y for a photo's `yaw`. three
 * looks an equirectangular image up with its centre column at +x; the
 * lesson's frame (lesson-schema's towardDirection) puts the column at `yaw`
 * straight ahead, at −z. A quarter turn more than the yaw joins the two.
 */
export function panoRotation(yaw: number): number {
  return THREE.MathUtils.degToRad(yaw + 90);
}

type Pixels = ArrayLike<number>;

const luminance = (pixels: Pixels, i: number): number =>
  0.2126 * pixels[i]! + 0.7152 * pixels[i + 1]! + 0.0722 * pixels[i + 2]!;

/**
 * The ground's colour: the median of the sample's rows 26–28, the ground
 * near the camera but not under it, where a dark tripod would pull an
 * average. sRGB bytes, as displayed.
 */
export function groundColour(
  pixels: Pixels,
  width: number = PANO_SAMPLE.width,
  height: number = PANO_SAMPLE.height,
): [number, number, number] {
  const scale = height / PANO_SAMPLE.height;
  const from = Math.round(PANO_SAMPLE.groundRows[0] * scale);
  const to = Math.round(PANO_SAMPLE.groundRows[1] * scale);
  const channels: number[][] = [[], [], []];
  for (let y = from; y <= to; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      for (let c = 0; c < 3; c++) channels[c]!.push(pixels[i + c]!);
    }
  }
  return channels.map((values) => {
    values.sort((a, b) => a - b);
    return values[Math.floor(values.length / 2)]!;
  }) as [number, number, number];
}

/**
 * Where the sun is, in the photo's own frame: the brightest 3 × 3 block of
 * the sample's upper half (wrapping round the seam), held 35–70° up. With
 * nothing 1.25 × brighter than the half's mean -- overcast, indoors -- the
 * light comes from 70° up, ahead.
 */
export function sunFrom(
  pixels: Pixels,
  width: number = PANO_SAMPLE.width,
  height: number = PANO_SAMPLE.height,
): { azimuth: number; elevation: number; overcast: boolean } {
  const upper = Math.floor(height / 2);
  let total = 0;
  for (let y = 0; y < upper; y++)
    for (let x = 0; x < width; x++) total += luminance(pixels, (y * width + x) * 4);
  const mean = total / (upper * width);

  let best = -1;
  let bestX = 0;
  let bestY = 1;
  for (let y = 1; y < upper - 1; y++) {
    for (let x = 0; x < width; x++) {
      let sum = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const xx = (x + dx + width) % width;
          sum += luminance(pixels, ((y + dy) * width + xx) * 4);
        }
      }
      if (sum / 9 > best) {
        best = sum / 9;
        bestX = x;
        bestY = y;
      }
    }
  }
  if (best < PANO_SUN.overcast * mean) return { ...PANO_SUN.fallback, overcast: true };
  return {
    azimuth: ((bestX + 0.5) / width - 0.5) * 360,
    elevation: THREE.MathUtils.clamp(
      (0.5 - (bestY + 0.5) / height) * 180,
      PANO_SUN.minElevation,
      PANO_SUN.maxElevation,
    ),
    overcast: false,
  };
}

/** The disc's alpha, RGBA: opaque to 4.8 m, a smoothstep to nothing at 6 m. */
export function discAlpha(size: number = PANO_DISC.alphaSize): Uint8Array {
  const data = new Uint8Array(size * size * 4);
  const half = size / 2;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const metres = (Math.hypot(x + 0.5 - half, y + 0.5 - half) / half) * PANO_DISC.radius;
      const alpha = 1 - THREE.MathUtils.smoothstep(metres, PANO_DISC.solid, PANO_DISC.radius);
      const value = Math.round(alpha * 255);
      data.set([value, value, value, 255], (y * size + x) * 4);
    }
  }
  return data;
}

/**
 * The background's six faces as they wait for the black: RGBA faces the pano
 * worker sampled from the JPEG (≈ 50 MB at 1440 px), or a cube's raw ASTC
 * blocks as the file holds them (11.2 MB at 2048 px, ADR-041).
 */
export type PreparedFaces =
  | (PanoFaces & { readonly kind: 'rgba' })
  | {
      readonly kind: 'astc';
      readonly size: number;
      /** Views into the fetched file, +X … −Z. */
      readonly data: readonly Uint8Array[];
      /** What the six take, here and on the GPU. */
      readonly bytes: number;
    };

/** A photo decoded and read, ready to go up at a step's black. Not on the GPU yet. */
export type PreparedPano = {
  /** The background's six faces, made off the main thread or read as they are; emptied on close(). */
  readonly faces: PreparedFaces;
  /** The 1024 × 512 copy the light is made from, flipped. */
  readonly light: ImageBitmap;
  /** The ground's colour, sRGB bytes. */
  readonly ground: readonly [number, number, number];
  /** The sun in the photo's own frame. */
  readonly sun: { azimuth: number; elevation: number; overcast: boolean };
  /** Lets go of the faces and the light's bitmap (safe to call twice). */
  close(): void;
};

export type PanoLoading = {
  /** The cube's and the light's too: their signed URLs. */
  fetchImpl?: (url: string) => Promise<Response>;
  decode?: (source: Blob, options: ImageBitmapOptions) => Promise<ImageBitmap>;
  /** The 64 × 32 sample's RGBA, top row first. */
  pixels?: (bitmap: ImageBitmap) => ArrayLike<number>;
  /** The six faces from the photo's blob, at most `maxFace` on a side; by default in a worker. */
  faces?: (blob: Blob, maxFace: number) => Promise<PanoFaces>;
  /** `renderer.capabilities.maxCubemapSize`: 2048 is WebGL 2's least, above 1440. */
  maxFace?: number;
  /**
   * Lets go of a cached file that turned out not to be the cube signed (a bad
   * file, or an older encode the service worker kept under the same path), so
   * the next fetch asks the API again. By default from the media cache.
   */
  forget?: (url: string) => Promise<unknown>;
};

/** Deletes a URL's entry from the service worker's media cache. Never throws. */
async function forgetCached(url: string): Promise<void> {
  try {
    const cache = await globalThis.caches?.open(MEDIA_CACHE);
    await cache?.delete(mediaCacheKey(url, globalThis.location?.origin ?? ''));
  } catch {
    // Only a cache: the JPEG shows either way.
  }
}

/**
 * One job in its own pano worker (pano-worker.ts), ended when the answer is
 * back so its memory goes (a photo's decoded pixels, about 130 MB for a
 * moment at 5760 px; a sky's float image). `transfer` hands the ask's buffers
 * over rather than copying them. Rejects with the worker's error.
 */
export function inPanoWorker(ask: PanoAsk, transfer: Transferable[] = []): Promise<PanoAnswer> {
  return new Promise<PanoAnswer>((resolve, reject) => {
    const worker = new Worker(new URL('./pano-worker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (event: MessageEvent<PanoAnswer>) => {
      worker.terminate();
      const answer = event.data;
      if ('error' in answer) reject(new Error(answer.error));
      else resolve(answer);
    };
    worker.onerror = (event) => {
      worker.terminate();
      reject(new Error(event.message || 'pano worker failed'));
    };
    worker.postMessage(ask, transfer);
  });
}

/**
 * The faces in the pano worker, one worker per photo. Without workers or
 * OffscreenCanvas -- no browser the headset targets -- on the page instead.
 */
export async function facesInWorker(blob: Blob, maxFace: number): Promise<PanoFaces> {
  if (typeof Worker !== 'function') return facesFromBlob(blob, maxFace);
  const answer = await inPanoWorker({ blob, maxFace });
  if (!('faces' in answer)) throw new Error('pano worker answered no faces');
  const { faces } = answer;
  console.info(
    `360° photo ${faces.width} × ${faces.height}: faces ${faces.size}² in ` +
      `${Math.round(faces.ms.decode)} ms decoding + ${Math.round(faces.ms.reproject)} ms sampling`,
  );
  return faces;
}

/**
 * The sample's pixels: through an OffscreenCanvas, or a page canvas in a
 * browser without one. The sample is 64 × 32, so either is instant.
 */
export function readPixels(bitmap: ImageBitmap): Uint8ClampedArray {
  const { width, height } = bitmap;
  let context: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
  if (typeof OffscreenCanvas === 'function') {
    context = new OffscreenCanvas(width, height).getContext('2d');
  } else {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    context = canvas.getContext('2d');
  }
  if (!context) throw new Error('no 2d context');
  context.drawImage(bitmap, 0, 0);
  return context.getImageData(0, 0, width, height).data;
}

type Loading = Required<Omit<PanoLoading, 'maxFace'>> & { maxFace: number };

function loading(options: PanoLoading): Loading {
  return {
    fetchImpl: options.fetchImpl ?? ((u: string) => fetch(u)),
    decode: options.decode ?? ((blob, o) => createImageBitmap(blob, o)),
    pixels: options.pixels ?? readPixels,
    faces: options.faces ?? facesInWorker,
    maxFace: options.maxFace ?? 4096,
    forget: options.forget ?? forgetCached,
  };
}

async function fetched(url: string, what: string, load: Loading): Promise<Response> {
  const response = await load.fetchImpl(url);
  if (!response.ok) throw new Error(`${what} ${response.status}`);
  return response;
}

/**
 * From a photo's blob (the JPEG, or its 1024 × 512 light copy): the light's
 * 1024 × 512 copy (flipped, kept) and the 64 × 32 sample the ground and the
 * sun are read from (closed at once), both by `createImageBitmap`. Whatever
 * bitmap it made goes into `made`, so a failure later can close it.
 */
async function lightFrom(
  blob: Blob,
  load: Loading,
  made: ImageBitmap[],
): Promise<Pick<PreparedPano, 'light' | 'ground' | 'sun'>> {
  const keep = (bitmap: ImageBitmap): ImageBitmap => {
    made.push(bitmap);
    return bitmap;
  };
  const light = keep(
    await load.decode(blob, {
      imageOrientation: 'flipY',
      premultiplyAlpha: 'none',
      resizeWidth: PANO_LIGHT.width,
      resizeHeight: PANO_LIGHT.height,
      resizeQuality: 'medium',
    }),
  );
  // Top row first, as the rows are counted: not flipped.
  const small = keep(
    await load.decode(blob, {
      premultiplyAlpha: 'none',
      resizeWidth: PANO_SAMPLE.width,
      resizeHeight: PANO_SAMPLE.height,
      resizeQuality: 'medium',
    }),
  );
  // Measured before it is closed: a closed bitmap is 0 × 0.
  const { width, height } = small;
  const data = load.pixels(small);
  small.close();
  return { light, ground: groundColour(data, width, height), sun: sunFrom(data, width, height) };
}

/** What preparing made, held until the black (or let go of unused). */
function held(
  faces: PreparedFaces,
  rest: Pick<PreparedPano, 'light' | 'ground' | 'sun'>,
): PreparedPano {
  const data = [...faces.data];
  let open = true;
  return {
    faces: { ...faces, data },
    ...rest,
    close() {
      if (!open) return;
      open = false;
      data.length = 0;
      rest.light.close();
    },
  };
}

/**
 * The JPEG path: fetches the photo and makes, from the one blob and off the
 * main thread, the light, the sample, and the background's six cube faces in
 * the pano worker. The full photo is never a bitmap on the page nor a texture
 * on the GPU (at 5760 × 2880 that is 66 MB): the worker decodes it and
 * samples the faces, about a second of a worker's time on a Quest during the
 * step before, and never a frame.
 */
async function prepareJpeg(url: string, load: Loading): Promise<PreparedPano> {
  const made: ImageBitmap[] = [];
  try {
    const blob = await (await fetched(url, 'photo', load)).blob();
    const rest = await lightFrom(blob, load, made);
    const cube = await load.faces(blob, load.maxFace);
    return held({ ...cube, kind: 'rgba' }, rest);
  } catch (error) {
    for (const bitmap of made) bitmap.close();
    throw error;
  }
}

/**
 * The cube path (ADR-041): the 1024 × 512 light copy first (≈ 0.1 MB) for the
 * light, the ground and the sun, as the JPEG path makes them from its own
 * copy; then the KTX2 cube, read as it is -- its header checked, its faces
 * views into the bytes, nothing decoded. What waits for the black is the
 * file's 11.2 MB, not 50 MB of RGBA.
 */
async function prepareCube(
  source: Extract<PanoSource, { kind: 'cube' }>,
  load: Loading,
): Promise<PreparedPano> {
  const made: ImageBitmap[] = [];
  try {
    const light = await (await fetched(source.light.url, 'light copy', load)).blob();
    const rest = await lightFrom(light, load, made);
    const file = await (await fetched(source.cube.url, 'cube', load)).arrayBuffer();
    let cube: AstcCube;
    try {
      cube = astcCube(file);
      // The cube signed, and no larger than the budget counts (worlds/budget.ts):
      // CacheFirst could hand back an older encode kept under the same path.
      const { face, bytes } = source.cube;
      if (cube.size !== face || cube.size > PANO_CUBE_FACE_LIMIT || file.byteLength !== bytes) {
        throw new Error(
          `cube is ${cube.size} px and ${file.byteLength} bytes, signed as ${face} px and ${bytes}`,
        );
      }
    } catch (error) {
      void load.forget(source.cube.url);
      throw error;
    }
    return held({ kind: 'astc', size: cube.size, data: cube.faces, bytes: cube.bytes }, rest);
  } catch (error) {
    for (const bitmap of made) bitmap.close();
    throw error;
  }
}

/**
 * Prepares a photo from its source (pano-source.ts). A cube that fails to
 * fetch or read falls back to the photo's JPEG, once; throws when that fails
 * too (the view then shows the studio), having closed whatever it made.
 */
export async function preparePano(
  source: PanoSource,
  options: PanoLoading = {},
): Promise<PreparedPano> {
  const load = loading(options);
  if (source.kind === 'jpeg') return prepareJpeg(source.url, load);
  try {
    return await prepareCube(source, load);
  } catch (error) {
    console.warn('360° photo: its cube could not be had, the JPEG instead', error);
    return prepareJpeg(source.jpeg, load);
  }
}

/** The stage in front of the photo: the ground disc and its shadow catcher. */
export type PanoStage = {
  readonly group: THREE.Group;
  readonly disc: THREE.Mesh<THREE.CircleGeometry, THREE.MeshBasicMaterial>;
  readonly shadow: THREE.Mesh<THREE.CircleGeometry, THREE.ShadowMaterial>;
  /** The ground's colour, sRGB bytes as the photo shows them. */
  setGround(rgb: readonly [number, number, number]): void;
  dispose(): void;
};

const noRaycast = (): void => {};

/**
 * The ground disc: 6 m at y = 0.002 in the colour of the photo's ground,
 * solid to 4.8 m and fading out by 6 m. From a 1.6 m eye at the origin the
 * solid part covers the photo below about 18° down, so the tripod and the
 * stitching at the nadir are never seen. Drawn before a lesson's things,
 * never pointed at, not tone-mapped (the photo is not). A 4.8 m shadow
 * catcher just above it catches the one soft shadow, as the studio's does.
 */
export function buildPanoStage(): PanoStage {
  const group = new THREE.Group();
  group.name = 'pano-stage';

  const alphaMap = new THREE.DataTexture(
    discAlpha(PANO_DISC.alphaSize),
    PANO_DISC.alphaSize,
    PANO_DISC.alphaSize,
  );
  alphaMap.magFilter = THREE.LinearFilter;
  alphaMap.minFilter = THREE.LinearMipmapLinearFilter;
  alphaMap.generateMipmaps = true;
  alphaMap.needsUpdate = true;
  const disc = new THREE.Mesh(
    new THREE.CircleGeometry(PANO_DISC.radius, PANO_DISC.segments),
    new THREE.MeshBasicMaterial({
      color: 0x808080,
      alphaMap,
      transparent: true,
      depthWrite: false,
      toneMapped: false,
      fog: false,
    }),
  );
  disc.name = 'pano-ground';
  disc.rotation.x = -Math.PI / 2;
  disc.position.y = PANO_DISC.y;
  disc.renderOrder = -1;
  disc.raycast = noRaycast;

  const shadow = new THREE.Mesh(
    new THREE.CircleGeometry(PANO_SHADOW.radius, PANO_DISC.segments),
    new THREE.ShadowMaterial({ opacity: PANO_SHADOW.opacity, depthWrite: false }),
  );
  shadow.name = 'pano-shadow';
  shadow.rotation.x = -Math.PI / 2;
  shadow.position.y = PANO_SHADOW.y;
  shadow.receiveShadow = true;
  shadow.raycast = noRaycast;

  group.add(disc, shadow);
  return {
    group,
    disc,
    shadow,
    setGround([r, g, b]) {
      disc.material.color.setRGB(r / 255, g / 255, b / 255, THREE.SRGBColorSpace);
    },
    dispose() {
      alphaMap.dispose();
      disc.geometry.dispose();
      disc.material.dispose();
      shadow.geometry.dispose();
      shadow.material.dispose();
      group.removeFromParent();
    },
  };
}

/**
 * Whose photo it is, on a plate on the disc 1.5 m behind the start, leaning
 * 30° off the floor towards it: a pupil who turns round reads it, as a
 * photographer's signature in the corner. Letters 5 cm, on a canvas of more
 * than 1024 px a metre; a long credit wraps and shrinks to fit (docs/04).
 */
export function creditPlate(
  credit: string,
  paint: Painter = paintOnCanvas,
): THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial> {
  const { width, height, canvas } = PANO_CREDIT;
  const texture = paint({
    width: canvas.width,
    height: canvas.height,
    background: 'rgba(12, 16, 24, 0.72)',
    lines: [
      {
        text: `360°-foto: ${credit}`,
        size: Math.round((PANO_CREDIT.letters * canvas.width) / width),
        colour: '#e8ecf2',
      },
    ],
  });
  const plate = new THREE.Mesh(
    new THREE.PlaneGeometry(width, height),
    new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      toneMapped: false,
      fog: false,
    }),
  );
  plate.name = 'pano-credit';
  const tilt = THREE.MathUtils.degToRad(PANO_CREDIT.tilt);
  // Facing the start (turned half round), leaning back until 30° off the floor.
  plate.rotation.set(-(Math.PI / 2 - tilt), Math.PI, 0, 'YXZ');
  plate.position.set(0, (height / 2) * Math.sin(tilt) + 0.006, PANO_CREDIT.behind);
  plate.raycast = noRaycast;
  return plate;
}

/** Something on the GPU that goes when it is let go. */
export type Uploaded = { readonly texture: THREE.Texture; dispose(): void };

/** The uploads, made only at a step's black (their hitch is never seen). */
export type PanoGpu = {
  /** The photo's six faces as the background's cube; the faces may be let go after. */
  background(faces: PanoFaces): Uploaded;
  /**
   * A cube's ASTC blocks as the background, as they are (ADR-041); the blocks
   * may be let go after. Throws on a GPU that cannot take them.
   */
  compressed(cube: AstcCube): Uploaded;
  /** The light: a PMREM of the 1024 × 512 copy; the bitmap may be closed after. */
  environment(light: ImageBitmap): Uploaded;
};

/**
 * The uploads through the renderer. The background goes up as its six faces
 * only (50 MB at 5760 px): three would turn an equirectangular background
 * into a cube the photo's height on a side and keep the photo as a 2D
 * texture beside it (66 MB more at 5760 px). The faces' bytes are let go
 * once they are up.
 */
export function rendererGpu(renderer: THREE.WebGLRenderer): PanoGpu {
  // A new lesson's: the HUD says nothing of the last one's photo.
  markPanoShown(undefined);
  return {
    background(faces) {
      const texture = cubeTexture(faces);
      const started = performance.now();
      renderer.initTexture(texture);
      markPanoShown({
        format: 'RGBA',
        size: faces.size,
        ms: performance.now() - started,
        bytes: faces.size * faces.size * 4 * 6,
      });
      // Up now, at the black: the page need not keep 50 MB of bytes for it.
      for (const face of texture.image as THREE.DataTexture[]) {
        face.image = { data: new Uint8Array(0), width: faces.size, height: faces.size };
      }
      return { texture, dispose: () => texture.dispose() };
    },
    compressed(cube) {
      if (!renderer.extensions.has(ASTC_EXTENSION)) throw new Error('this GPU takes no ASTC');
      const texture = compressedCubeTexture(cube);
      const started = performance.now();
      try {
        renderer.initTexture(texture);
      } catch (error) {
        // Whatever it allocated goes; the view stands the studio in.
        texture.dispose();
        throw error;
      }
      markPanoShown({
        format: 'ASTC_6x6',
        size: cube.size,
        ms: performance.now() - started,
        bytes: cube.bytes,
      });
      // Up now, at the black: the page need not keep the file's 11 MB for it.
      for (const face of texture.image as unknown as { mipmaps: { data: Uint8Array }[] }[]) {
        for (const level of face.mipmaps) level.data = new Uint8Array(0);
      }
      return { texture, dispose: () => texture.dispose() };
    },
    environment(light) {
      const source = photoTexture(light);
      const pmrem = new THREE.PMREMGenerator(renderer);
      const target = pmrem.fromEquirectangular(source);
      pmrem.dispose();
      source.dispose();
      return { texture: target.texture, dispose: () => target.dispose() };
    },
  };
}

/** The stage's black, for a late photo: held with a hint, let go with a fade in. */
export type Black = { hold(hint: string): void; release(): void };

export type PanoViewOptions = {
  readonly lesson: Lesson;
  /** Media id -> signed URL, as the lesson came with it. */
  readonly media: Readonly<Record<string, string>>;
  /** Media id -> its signed cube and light copy (ADR-041); `{}` without. */
  readonly variants?: Readonly<PanoVariants>;
  /** What the GPU takes (pano-caps.ts); without, the JPEG path. */
  readonly caps?: PanoCaps;
  readonly scene: THREE.Scene;
  /** Absent without a renderer: every photo then shows as the studio. */
  readonly gpu?: PanoGpu;
  readonly prepare?: (source: PanoSource) => Promise<PreparedPano>;
  /** A timer, in seconds; tests replace it. */
  readonly schedule?: (run: () => void, seconds: number) => () => void;
  /** `?look=flat`: the photo without PMREM light or shadow, lit by the studio's lights. */
  readonly flat?: boolean;
  /** The studio that stands in for a photo (its floor, sky and lights), and its sky light. */
  readonly studio: {
    readonly root: THREE.Object3D;
    readonly lights?: readonly THREE.Light[];
    readonly environment?: () => THREE.Texture | null;
    readonly environmentIntensity?: number;
  };
  redrawShadow(): void;
  /** The desktop note when the studio stands in for a photo; `undefined` once a photo shows. */
  onNote?(text: string | undefined): void;
  readonly paint?: Painter;
};

type Entry = {
  readonly key: string;
  state: 'loading' | 'ready' | 'failed';
  prepared?: PreparedPano;
  /** Settles (never rejects) when the photo is in or has failed. */
  settled: Promise<void>;
};

/** What a step shows: its photo, or the studio and why. */
type Display =
  | { kind: 'photo'; key: string; source: PanoSource; photo: PanoPhoto }
  | { kind: 'studio'; reason: 'unchosen' | 'failed' };

type Shown = {
  readonly mode: 'none' | 'photo' | 'studio';
  /** The photo's media id; for a late studio, the photo it stands in for. */
  readonly key?: string;
  /** The studio stands in for a photo that was not in at the black. */
  readonly late: boolean;
};

const defaultSchedule = (run: () => void, seconds: number): (() => void) => {
  const timer = setTimeout(run, seconds * 1000);
  return () => clearTimeout(timer);
};

/**
 * A pano lesson's photos, step by step (ADR-036): which is shown, which is
 * held, and what happens when one is late or cannot be had.
 *
 * - The lesson's start shows the photo in effect at step 0; `ready` settles
 *   once it is shown, failed, or 15 s have passed (then the studio, and the
 *   photo at the next fade).
 * - At a step change the headset asks `differs`; a different photo always
 *   travels through black, where `arrive` swaps it: the background, the
 *   light, the sun, the ground's colour and the credit plate.
 * - Not ready at the black: the black holds up to 3 s ("De foto wordt
 *   geladen…" after 2 s), then the studio until the next fade.
 * - When a step's fade clears, `preload` prepares the next step's photo. At
 *   most two photos are held: the one shown (on the GPU) and the next
 *   (its RGBA faces, or its cube's ASTC blocks as fetched, and its light).
 * - A photo that cannot be had -- no URL, a failed fetch or decode, a wish
 *   nobody has filled -- shows the studio for its steps, with a desktop note.
 */
export class PanoView {
  /** The disc, the shadow catcher, the credit plate, the sun and the studio. */
  readonly root = new THREE.Group();
  readonly ready: Promise<void>;

  readonly #options: PanoViewOptions;
  readonly #schedule: (run: () => void, seconds: number) => () => void;
  readonly #stage = buildPanoStage();
  readonly #studio = new THREE.Group();
  readonly #entries = new Map<string, Entry>();
  #shown: Shown = { mode: 'none', late: false };
  #showing:
    | {
        background: Uploaded;
        environment?: Uploaded;
        sun?: THREE.DirectionalLight;
        credit?: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
      }
    | undefined;
  #started = false;
  #disposed = false;
  /** The step the start (or a hold) is waiting for. */
  #wanted = 0;
  /** The photo a black is waiting for, the photo preloaded. */
  #arriving: string | undefined;
  #next: string | undefined;
  #holding: Black | undefined;
  #token = 0;
  #cancelWait: (() => void) | undefined;
  /** Photos whose cube the GPU refused at the black: their JPEG from then on. */
  readonly #noCube = new Set<string>();

  constructor(options: PanoViewOptions) {
    this.#options = options;
    this.#schedule = options.schedule ?? defaultSchedule;
    this.root.name = 'pano';
    this.#studio.name = 'pano-studio';
    this.#studio.add(options.studio.root, ...(options.studio.lights ?? []));
    this.#studio.visible = false;
    this.#stage.group.visible = false;
    this.root.add(this.#stage.group, this.#studio);
    this.ready = this.#start();
  }

  /** What is shown now. */
  get shown(): Shown {
    return this.#shown;
  }

  /** The photo's yaw at step `index`: where its front is (0 by default). */
  yawAt(index: number): number {
    return panoAt(this.#options.lesson, index)?.photo.yaw ?? 0;
  }

  /** Whether step `index`'s own photo is what is shown (direction badges show only then). */
  showsPhotoAt(index: number): boolean {
    const display = this.#display(index);
    return (
      display.kind === 'photo' && this.#shown.mode === 'photo' && this.#shown.key === display.key
    );
  }

  /** Photos held: the one shown, and any decoded and waiting. */
  held(): number {
    let held = this.#showing ? 1 : 0;
    for (const entry of this.#entries.values()) if (entry.prepared) held++;
    return held;
  }

  /**
   * Whether step `index` shows something else than what is shown now, so its
   * change must go through black: another photo, the studio for a photo or a
   * photo for the studio -- or a late photo that has arrived since.
   */
  differs(index: number): boolean {
    if (!this.#started) return false;
    const display = this.#display(index);
    const shown = this.#shown;
    if (display.kind === 'studio') return shown.mode !== 'studio';
    if (shown.mode === 'photo') return shown.key !== display.key;
    if (shown.late && shown.key === display.key) {
      return this.#entries.get(display.key)?.state === 'ready';
    }
    return true;
  }

  /**
   * At a step's black (every fade): step `index`'s photo goes in, or the
   * studio; a photo not yet in holds the black up to 3 s. Before the
   * lesson's start is done, only notes which step the start should show.
   */
  arrive(index: number, black: Black): void {
    if (this.#disposed) return;
    if (!this.#started) {
      this.#wanted = index;
      const display = this.#display(index);
      if (display.kind === 'photo') this.#load(display);
      return;
    }
    this.#stopWaiting();
    const display = this.#display(index);
    if (display.kind === 'studio') {
      this.#arriving = undefined;
      this.#showStudio(display.reason);
      this.#release();
      return;
    }
    if (this.#shown.mode === 'photo' && this.#shown.key === display.key) {
      this.#arriving = undefined;
      this.#release();
      return;
    }
    this.#arriving = display.key;
    const entry = this.#load(display);
    if (entry.state !== 'loading') {
      this.#finish(display);
      return;
    }
    // Still standing in for it, and still not in: the studio stays, unseen
    // change -- and a black held for another step's photo lets go.
    if (this.#shown.late && this.#shown.key === display.key) {
      this.#release();
      return;
    }
    if (!this.#holding) {
      this.#holding = black;
      black.hold(PANO_HINT);
    }
    const token = ++this.#token;
    const finish = (): void => {
      if (token !== this.#token || this.#disposed) return;
      this.#stopWaiting();
      this.#finish(display);
    };
    void entry.settled.then(finish);
    this.#cancelWait = this.#schedule(finish, PANO_WAIT_SECONDS);
  }

  /** When a step's fade clears: step `index`'s photo is prepared, if it differs. */
  preload(index: number): void {
    if (this.#disposed) return;
    const display = this.#display(index);
    if (display.kind === 'studio') return;
    if (this.#shown.mode === 'photo' && this.#shown.key === display.key) return;
    this.#next = display.key;
    // At most two: the one shown and this one.
    for (const [key, entry] of this.#entries) {
      if (!this.#wants(key)) this.#drop(key, entry);
    }
    this.#load(display);
  }

  dispose(): void {
    this.#disposed = true;
    this.#stopWaiting();
    this.#free();
    for (const [key, entry] of this.#entries) this.#drop(key, entry);
    this.#stage.dispose();
    // The studio it was given is its own now: its floor, sky and lights go too.
    this.#studio.traverse((node) => {
      if (node instanceof THREE.Light) node.dispose();
      const drawn = node as THREE.Mesh<THREE.BufferGeometry, THREE.Material | THREE.Material[]>;
      if (!(node instanceof THREE.Mesh || node instanceof THREE.Line)) return;
      drawn.geometry.dispose();
      for (const m of Array.isArray(drawn.material) ? drawn.material : [drawn.material]) {
        m.dispose();
      }
    });
    const { scene } = this.#options;
    scene.background = null;
    scene.environment = null;
    this.root.removeFromParent();
  }

  /** The start: the photo at step 0 (or wherever the class is by then), 15 s at most. */
  async #start(): Promise<void> {
    let late = false;
    let wake: () => void = () => {};
    const deadline = new Promise<void>((resolve) => (wake = resolve));
    const cancel = this.#schedule(() => {
      late = true;
      wake();
    }, PANO_START_SECONDS);
    for (;;) {
      const index = this.#wanted;
      const display = this.#display(index);
      if (display.kind === 'studio') break;
      this.#arriving = display.key;
      await Promise.race([this.#load(display).settled, deadline]);
      if (late || this.#wanted === index || this.#disposed) break;
    }
    cancel();
    this.#started = true;
    if (this.#disposed) return;
    this.#finish(this.#display(this.#wanted));
  }

  /** Shows what a step's display can show now: its photo if in, else the studio. */
  #finish(display: Display): void {
    this.#release();
    if (display.kind === 'studio') {
      this.#showStudio(display.reason);
      return;
    }
    const entry = this.#entries.get(display.key);
    if (entry?.state === 'ready' && entry.prepared) this.#showPhoto(display, entry);
    else if (entry?.state === 'failed') this.#showStudio('failed');
    else this.#showStudio('late', display.key);
  }

  #display(index: number): Display {
    const at = panoAt(this.#options.lesson, index);
    const photo = at?.photo ?? {};
    if (photo.media === undefined) return { kind: 'studio', reason: 'unchosen' };
    const { media, variants = {}, caps = NO_PANO_CAPS, gpu } = this.#options;
    let source = panoSource(photo.media, media, variants, caps);
    if (source === undefined || !gpu) return { kind: 'studio', reason: 'failed' };
    if (this.#entries.get(photo.media)?.state === 'failed') {
      return { kind: 'studio', reason: 'failed' };
    }
    if (source.kind === 'cube' && this.#noCube.has(photo.media)) {
      source = { kind: 'jpeg', url: source.jpeg };
    }
    return { kind: 'photo', key: photo.media, source, photo };
  }

  #wants(key: string): boolean {
    return (
      key === this.#next || key === this.#arriving || (this.#shown.late && this.#shown.key === key)
    );
  }

  #load(display: Extract<Display, { kind: 'photo' }>): Entry {
    const known = this.#entries.get(display.key);
    if (known) return known;
    const prepare = this.#options.prepare ?? ((source: PanoSource) => preparePano(source));
    const entry: Entry = { key: display.key, state: 'loading', settled: Promise.resolve() };
    entry.settled = prepare(display.source).then(
      (prepared) => {
        if (this.#disposed || this.#entries.get(display.key) !== entry) {
          prepared.close();
          return;
        }
        if (!this.#wants(display.key)) {
          prepared.close();
          this.#entries.delete(display.key);
          return;
        }
        entry.state = 'ready';
        entry.prepared = prepared;
      },
      (error: unknown) => {
        console.warn(`360° photo ${display.key} could not be loaded`, error);
        entry.state = 'failed';
      },
    );
    this.#entries.set(display.key, entry);
    return entry;
  }

  #drop(key: string, entry: Entry): void {
    entry.prepared?.close();
    // A failure stays known, so the photo is not asked for again.
    if (entry.state !== 'failed') this.#entries.delete(key);
  }

  #release(): void {
    const black = this.#holding;
    this.#holding = undefined;
    black?.release();
  }

  #stopWaiting(): void {
    this.#token++;
    this.#cancelWait?.();
    this.#cancelWait = undefined;
  }

  /** Lets go of what the shown photo holds on the GPU. */
  #free(): void {
    const showing = this.#showing;
    this.#showing = undefined;
    if (!showing) return;
    showing.background.dispose();
    showing.environment?.dispose();
    if (showing.sun) {
      showing.sun.removeFromParent();
      showing.sun.target.removeFromParent();
      showing.sun.dispose();
    }
    if (showing.credit) {
      showing.credit.removeFromParent();
      showing.credit.material.map?.dispose();
      showing.credit.material.dispose();
      showing.credit.geometry.dispose();
    }
  }

  #showPhoto(display: Extract<Display, { kind: 'photo' }>, entry: Entry): void {
    const prepared = entry.prepared!;
    const { gpu, scene, flat } = this.#options;
    this.#entries.delete(entry.key);
    if (this.#next === entry.key) this.#next = undefined;
    if (this.#arriving === entry.key) this.#arriving = undefined;
    // The old one first: it is black, and the two never need to be up at once.
    this.#free();
    // A studio that stood in keeps its key light's shadow map until told
    // (8 MB, measured); drawn again should it stand in once more. Before the
    // uploads, so the peak of a change is not raised by it.
    if (!flat) {
      for (const light of this.#options.studio.lights ?? []) {
        const shadow = (light as THREE.DirectionalLight).shadow as THREE.LightShadow | undefined;
        if (!shadow?.map) continue;
        shadow.map.dispose();
        shadow.map = null;
      }
    }
    const { faces } = prepared;
    let background: Uploaded;
    try {
      background =
        faces.kind === 'astc'
          ? gpu!.compressed({ size: faces.size, faces: faces.data, bytes: faces.bytes })
          : gpu!.background(faces);
    } catch (error) {
      prepared.close();
      this.#refused(display, faces.kind === 'astc', error);
      return;
    }
    const environment = flat ? undefined : gpu!.environment(prepared.light);
    prepared.close();

    const yaw = display.photo.yaw ?? 0;
    const turn = panoRotation(yaw);
    scene.background = background.texture;
    scene.backgroundRotation.set(0, turn, 0);
    scene.environmentRotation.set(0, turn, 0);
    scene.environment = environment?.texture ?? null;
    scene.environmentIntensity = 1;

    let sun: THREE.DirectionalLight | undefined;
    if (!flat) {
      const [x, y, z] = towardDirection([prepared.sun.azimuth, prepared.sun.elevation], yaw);
      sun = makeSun(new THREE.Vector3(x, y, z), 0xffffff, PANO_SUN.intensity);
      this.root.add(sun, sun.target);
    }
    const credit =
      display.photo.credit === undefined
        ? undefined
        : creditPlate(display.photo.credit, this.#options.paint);
    if (credit) this.root.add(credit);

    this.#stage.setGround(prepared.ground);
    this.#stage.group.visible = true;
    this.#stage.shadow.visible = !flat;
    // Flat: lit by the studio's lights, without its floor and sky in front of the photo.
    this.#studio.visible = flat === true;
    this.#options.studio.root.visible = false;
    this.#showing = {
      background,
      ...(environment ? { environment } : {}),
      ...(sun ? { sun } : {}),
      ...(credit ? { credit } : {}),
    };
    this.#options.onNote?.(undefined);
    this.#shown = { mode: 'photo', key: entry.key, late: false };
    this.#options.redrawShadow();
  }

  /**
   * The GPU would not take a photo's background at the black. A cube: the
   * studio stands in while its JPEG is prepared, which goes in at the next
   * fade. The JPEG too: the studio for its steps, as for a failed fetch.
   */
  #refused(display: Extract<Display, { kind: 'photo' }>, cube: boolean, error: unknown): void {
    console.warn(`360° photo ${display.key} did not go up`, error);
    if (cube && display.source.kind === 'cube' && !this.#noCube.has(display.key)) {
      this.#noCube.add(display.key);
      this.#showStudio('late', display.key);
      this.#load({ ...display, source: { kind: 'jpeg', url: display.source.jpeg } });
      return;
    }
    this.#entries.set(display.key, {
      key: display.key,
      state: 'failed',
      settled: Promise.resolve(),
    });
    this.#showStudio('failed');
  }

  #showStudio(reason: 'unchosen' | 'failed' | 'late', key?: string): void {
    const { scene, studio } = this.#options;
    this.#free();
    scene.background = new THREE.Color(STUDIO_HORIZON);
    scene.backgroundRotation.set(0, 0, 0);
    scene.environmentRotation.set(0, 0, 0);
    scene.environment = this.#options.flat ? null : (studio.environment?.() ?? null);
    scene.environmentIntensity = studio.environmentIntensity ?? 1;
    this.#stage.group.visible = false;
    this.#studio.visible = true;
    studio.root.visible = true;
    this.#shown = {
      mode: 'studio',
      late: reason === 'late',
      ...(reason === 'late' && key !== undefined ? { key } : {}),
    };
    this.#options.onNote?.(PANO_NOTE[reason]);
    this.#options.redrawShadow();
  }
}
